import { useQueries } from '@tanstack/react-query';
import { useLanguageStore } from '@/context/LanguageStore';
import axios from 'axios';
import consts from '@/config/consts';
import { isProduct } from '@/helpers/wizard';

/**
 * Takes an array of WizardItems, checks startpage.json availability
 * for each product-type item, and returns only those that exist.
 * Non-product items (categories) pass through unfiltered.
 *
 * Returns empty list while loading so the UI can show a spinner,
 * preventing partial/flickering results.
 */
export function useValidProducts(items: Elvita.WizardItem[] | undefined) {
  const lang = useLanguageStore(state => state.lang);

  const productItems = (items ?? []).filter(item => isProduct(item));

  const queries = useQueries({
    queries: productItems.map(item => ({
      queryKey: ['product-exists', item.validity, lang],
      queryFn: async (): Promise<boolean> => {
        try {
          await axios.get(
            `/manifests/${item.validity}_${lang}/startpage.json`,
            { baseURL: consts.storageUrl },
          );
          return true;
        } catch {
          return false;
        }
      },
      enabled: !!item.validity && !!lang,
      retry: false,
      staleTime: Infinity,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    })),
  });

  // Wait until ALL queries have settled before filtering
  const isLoading = queries.length > 0 && queries.some(q => q.isPending);

  if (isLoading) {
    return { validItems: [] as Elvita.WizardItem[], isLoading: true };
  }

  const validSet = new Set<number>();
  queries.forEach((q, i) => {
    if (q.data === true) {
      validSet.add(productItems[i].validity);
    }
  });

  const validItems = (items ?? []).filter(item => {
    if (!isProduct(item)) return true;
    return validSet.has(item.validity);
  });

  return { validItems, isLoading: false };
}
